<template>
  <SfMessageOuterContainer
    :id="`remark-${remark.id}`"
    v-click-outside="{ handler: handleClickOutside, detectIframe: false }"
    :dimmed="isOtherCardHighlighted"
    :highlighted="highlighted"
    :subtle-background="remark.todo && remark.done && !slim"
    :data-liquid-input-field-path="remark.liquidInputFieldPath"
    :data-text-property-value="remark.textPropertyValue"
    :data-testid="testid"
    :type="remarkType"
    :slim="slim"
    :bleed="theme.newLayout"
    @click.stop="handleContainerClick"
  >
    <RemarkBody
      :id="remark.id"
      :class="theme.newLayout && 'tw-px-6 tw-pt-4'"
      :sticky="remark.sticky"
      :recurring="!!remark.recurringTodoTemplateId"
      :show-context="showContext"
      :context-text="contextName"
      :context-link="contextUrl"
      :context-type="remark.contextType"
      :ledger-period-name="remark.ledgerPeriodName"
      :liquid-input-field-path="remark.liquidInputFieldPath"
      :author-name="authorName"
      :created-at="remark.createdAt"
      :edited-at="remark.editedAt"
      :rendered-text="renderedTextWithOrdinal"
      :attachments="attachments"
      :allowed-actions="{
        edit: remark.allowedActions.edit,
        openInContext: showContext && showContextBtn,
      }"
      :prevent-bubble-unlink="preventBubbleUnlink"
      :show-period="showPeriod"
      :has-check-diff="remark.hashCheckDiff"
      :has-draft="hasDraft"
      :read="remarkRead"
      :done="remark.done"
      :done-at="remark.doneAt"
      :done-by-name="remark.doneByName"
      :todo="remark.todo"
      :part-of="partOf"
      :pdf-annotation="remark.pdfAnnotation"
      :requested-for-owner="remark.requestedForOwner"
      :document-filename="remark.documentFilename"
      :slim="slim"
      :report-element-name="remark.reportElementName"
      :enable-element-icon-scroll="enableElementIconScroll"
      :pulse-mention-warning="remark.pulseMentionWarning"
      @delete-attachment="deleteAttachment"
      @visit-annotation="gotoPdfAnnotation"
      @scroll-to-element="scrollToElement"
    >
      <template v-if="remark.todo && !slim" #top-left>
        <ToDo
          :done="remark.done"
          :can-edit="remark.allowedActions.toggleDone && !disableInteraction"
          @toggle="toggleTodo"
        />
      </template>
      <template v-if="showDiscussedInMeetingLink" #after-remark-text>
        <div
          class="tw-uppercase tw-text-xs tw-font-medium"
          data-testid="client-meeting-link"
        >
          {{ I18n.t('communication.cards.used_in_client_meeting.used_in') }}
          <a
            v-tooltip="clientMeetingLinkTooltip"
            data-heap="remark-used-in-client-meeting-link"
            @click.prevent="handleDiscussedInMeetingLinkClick"
          >
            {{ I18n.t('communication.cards.used_in_client_meeting.a_meeting') }}
          </a>
        </div>
      </template>

      <template v-if="!slim" #before-message>
        <EditableRemarkTagsList
          :editing="editingTags"
          :wrap="wrapTags"
          :tag-ids="remark.tagIds"
          :exclude-tag-ids-in-card="excludeTagIdsInCard"
          :remark-id="remark.id"
          :is-client-meeting-remark="remark.contextType === 'ClientMeeting'"
          :class="hideSessionClass"
          :disable-links="disableTagLinks"
          @stop-editing="stopEditingTags"
        />
      </template>
      <template #actions>
        <RemarkActions
          :allowed-actions="{
            updateTags: canEditTags && !disableInteraction,
            edit: remark.allowedActions.edit && !disableInteraction,
            openInContext: showContextBtn && contextUrl && !disableInteraction,
            remove: remark.allowedActions.remove && !disableInteraction,
            toggleRead: !hideReadStatus && !disableInteraction,
          }"
          :read="read"
          @click="handleRemarkAction"
        />
        <!-- In slim/presentation mode, show reply button next to actions when there are no comments -->
        <SfButtonDs
          v-if="slim && !disableInteraction && !comments.length"
          size="sm"
          data-testid="remark-reply"
          @click="startReply"
        >
          {{ I18n.t('common.reply') }}
        </SfButtonDs>
      </template>
    </RemarkBody>
    <!-- tw-pb-4 closes the card when no section below owns its own bottom
         padding. The reply form brings its own, and so does the footer row. -->
    <div
      class="tw-flex tw-flex-col"
      data-testid="remark-card-sections"
      :class="{
        'tw-gap-2': !theme.newLayout,
        'tw-pb-4':
          theme.newLayout &&
          !slim &&
          !showReplyFooter() &&
          !showNewCommentForm &&
          !lastCommentEditing,
      }"
    >
      <div :class="commentsOpacityClass">
        <SfCommentList
          v-if="hasCommentList()"
          ref="commentList"
          :toggleable-count="toggleableComments.length"
          :show-mark-as-read="!read"
          :slim="slim"
          :bleed="theme.newLayout"
          :class="{
            'tw-pb-4': slim && (!theme.newLayout || !showNewCommentForm),
          }"
          @mark-as-read="toggleRead"
        >
          <template #toggleable>
            <RemarkComment
              v-for="comment in toggleableComments"
              :key="comment.id"
              v-bind="commentContext"
              :comment="comment"
              :unread-top-offset="comment.isFirstUnread"
              :border="
                theme.newLayout &&
                (!slim || comment.last || (!showNewCommentForm && read))
              "
              @delete-attachment="
                (attachmentId) =>
                  deleteCommentAttachment(comment.id, attachmentId)
              "
              @action="(action) => handleCommentAction(action, comment.id)"
            />
          </template>
          <template #default="slotProps">
            <RemarkComment
              v-for="comment in visibleComments"
              :key="comment.id"
              v-bind="commentContext"
              :comment="comment"
              :unread-top-offset="
                comment.isFirstUnread || (comment.first && !slotProps.expanded)
              "
              :unread-bottom-offset="!comment.last"
              :border="theme.newLayout && comment.last"
              @delete-attachment="
                (attachmentId) =>
                  deleteCommentAttachment(comment.id, attachmentId)
              "
              @action="(action) => handleCommentAction(action, comment.id)"
            />
            <slot name="after-comments"></slot>
          </template>
        </SfCommentList>
      </div>
      <div
        v-if="!theme.newLayout || showReplyFooter()"
        class="tw-flex tw-justify-between"
        :class="{
          [DividerClasses.TOP]: theme.newLayout && !slim,
          'tw-px-3 tw-py-2':
            theme.newLayout && (!slim || !!slots['custom-actions']),
        }"
      >
        <slot name="custom-actions"></slot>
        <SfButtonDs
          v-if="showReplyButton"
          :class="
            slim
              ? 'tw-opacity-0 group-hover:tw-opacity-100 tw-ms-auto tw-absolute tw-bottom-0 tw-right-0 tw-mb-5 tw-mr-6 sf-btn-shadow-overlay'
              : 'tw-ms-auto'
          "
          size="sm"
          data-testid="remark-reply"
          @click="startReply"
        >
          {{ I18n.t('common.reply') }}
        </SfButtonDs>
      </div>

      <div
        v-if="showNewCommentForm"
        :class="theme.newLayout && DividerClasses.TOP"
        data-testid="remark-card-reply-form"
      >
        <CommentForm
          commentable-type="Remark"
          :commentable-id="remark.id"
          :company-id="remark.companyId"
          :ledger-id="remark.ledgerId"
          :period-name="remark.privacyPeriodName"
          :period-private="remark.periodPrivate"
          :follower-ids="remark.followerIds"
          :btn-text="I18n.t('common.reply')"
          :variant="theme.newLayout ? 'seamless' : 'default'"
          round-bottom
          :placeholder="
            theme.newLayout ? I18n.t('communication.comments.enter_reply') : ''
          "
          text=""
        />
      </div>
    </div>
  </SfMessageOuterContainer>
</template>

<script setup>
import orderBy from 'lodash/orderBy'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
} from 'vue'
import { useStore } from 'vuex'

import SfMessageOuterContainer from '../../sf-message/sf-message-outer-container.vue'
import { isCurrentWorkflowLocked } from '@app/adapters/silverfin'
import api from '@app/application/communication/sections/communication/api/remark'
import RemarkBody from '@app/shared/vue/components/molecules/remark/remark-body.vue'
import RemarkComment from '@app/shared/vue/components/molecules/remark/remark-comment.vue'
import SfCommentList from '@app/shared/vue/components/molecules/remark/sf-comment-list.vue'
import ToDo from '@app/shared/vue/components/molecules/remark/to-do.vue'
import { REMARK_ACTIONS } from '@app/application/communication/constants'
import useI18n from '@app/shared/vue/composables/use-I18n'
import CommentForm from '@app/application/communication/sections/communication/components/comments/comment-form.vue'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import { SfLog } from '@app/adapters/logger'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { commsTheme } from '@app/shared/comms-theme'
import RemarkActions from '@app/shared/vue/components/molecules/remark/remark-actions.vue'
import trackEvent from '@app/utils/track_event'
import useRemarkDraft from '@app/shared/vue/composables/use-remark-draft'
import { pickAttachmentsByIds } from '@app/shared/vue/components/molecules/remark/utils'
import EditableRemarkTagsList from '@app/shared/vue/components/molecules/remark/editable-remark-tags-list.vue'

import { useHighlightedCard } from '@app/shared/pinia/highlighted-card'
import { storeToRefs } from 'pinia'
import { useNavigationStore } from '@app/shared/vue/store/navigation-store'
import { RemarkContextTypes } from '@app/application/communication/types'
import { DividerClasses } from '@app/shared/constants'

const theme = commsTheme()

const store = useStore()
const I18n = useI18n()
const hideSessionClass = useRedactSessionRecording()
const slots = useSlots()

const props = defineProps({
  preventBubbleUnlink: Boolean,
  remark: { type: Object, required: true },
  showContext: Boolean,
  showContextBtn: Boolean,
  showPeriod: Boolean,
  openReply: Boolean,
  hideReadStatus: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableTagLinks: {
    type: Boolean,
    required: false,
    default: false,
  },
  showTags: Boolean,
  partOf: { type: String, required: false, default: null },
  editModalSize: { type: String, required: false, default: null },
  slim: { type: Boolean, required: false, default: false },
  enableElementIconScroll: {
    type: Boolean,
    required: false,
    default: false,
  },
  presentationComments: { type: Object, required: false, default: null },
  ordinal: { type: Number, required: false, default: null },
  excludeTagIdsInCard: {
    type: Array,
    required: false,
    default: () => [],
  },
})

const highlightCardStore = useHighlightedCard()
const { highlightedCard } = storeToRefs(highlightCardStore)

const navigationStore = useNavigationStore()

const { message: draftMessage, load: reloadDraft } = useRemarkDraft({
  id: props.remark.id,
  ledgerId: props.remark.ledgerId,
})

const remarkModalWasOpened = ref(false)
const commentList = ref(null)

const wrapTags = computed(() => props.partOf !== 'inbox')

const testid = computed(() => {
  if (props.remark.todo) {
    if (props.remark.done) {
      return 'remark-card-done'
    } else {
      return 'remark-card-todo'
    }
  } else {
    return 'remark-card-note'
  }
})

const commentsOpacityClass = computed(() => ({
  'tw-opacity-70': props.remark.todo && props.remark.done,
}))

const showNewCommentForm = computed(
  () =>
    props.openReply ||
    store.state.ui.newCommentRemarkIds.includes(props.remark.id)
)

const editingComment = computed(() => comments.value.some((c) => c.editing))

// Only the last comment's edit form sits at the foot of the card and brings its
// own padding. Editing any earlier one leaves the comments band at the bottom,
// which still needs the card's padding to keep its square corners off the
// card's rounded ones.
const lastCommentEditing = computed(
  () => comments.value.at(-1)?.editing ?? false
)

const showReplyButton = computed(
  () =>
    !showNewCommentForm.value &&
    !disableInteraction &&
    (!props.slim || comments.value.length > 0) &&
    !editingComment.value
)
// Read per render, as slots are not a reactive dependency.
function hasCommentList() {
  return !!comments.value.length || !!slots['after-comments']
}

// Read per render: the slot's presence is not a reactive dependency, and both
// the footer and the card's bottom padding have to agree on it.
function showReplyFooter() {
  return (
    showReplyButton.value || (!!slots['custom-actions'] && !disableInteraction)
  )
}

const canEditTags = computed(() => {
  const anyUnarchivedTags = Object.values(
    store.state.remarkTags?.allRemarkTags || {}
  ).some((t) => !t.archived)
  return (
    props.remark.allowedActions.updateTags &&
    (anyUnarchivedTags || props.remark.tagIds.length > 0)
  )
})

const context = computed(() =>
  store.getters.contextByLedgerTypeAndId(
    store.state.ledgerId || props.remark.ledgerId,
    props.remark.contextType,
    props.remark.contextId
  )
)
const contextName = computed(() => context.value?.name ?? undefined)
const contextUrl = computed(() => {
  if (!context.value) {
    return null
  }

  if (context.value.workflowReviewUrl) {
    return context.value.workflowReviewUrl
  }
  if (!context.value.url) {
    return null
  }
  const url = new URL(context.value.url, window.location.origin)
  if (!props.remark.ledgerArchived) {
    const ledgerId = store.state.ledgerId || props.remark.ledgerId
    url.searchParams.set('current_ledger_id', `${ledgerId}`)
  }
  if (isInCompanyWorkflowSubType.value) {
    url.searchParams.set('anchor', `remark-${props.remark.id}`)
  } else {
    url.hash = `remark-${props.remark.id}`
  }
  return url.toString()
})

const showDiscussedInMeetingLink =
  props.partOf !== 'past_meetings' &&
  props.partOf !== 'meeting_preparation' &&
  !!props.remark.companyId &&
  !!props.remark.clientMeetingId &&
  !!props.remark.id

const handleDiscussedInMeetingLinkClick = (event) => {
  const url = window.Silverfin.Routes.client_meetings_past_path(
    window.Silverfin.currentFirmId,
    props.remark.companyId,
    { anchor: 'remark-' + props.remark.id }
  )

  const shouldOpenInNewTab = event.metaKey || event.ctrlKey
  if (shouldOpenInNewTab) {
    window.open(url, '_blank')
  } else {
    const currentLocation = window.location.href

    window.Turbolinks.visit(url)

    navigationStore.enableBackInContext(
      currentLocation,
      'remark_tag',
      props.remark.todo ? 'todo' : 'note'
    )
  }
}

const clientMeetingLinkTooltip = computed(() => {
  return window.I18n.t('communication.cards.used_in_client_meeting.tooltip')
})

const hasDraft = computed(
  () => !!draftMessage.value && draftMessage.value !== props.remark.text
)

const highlighted = computed(() => {
  return (
    highlightedCard.value.type === 'remark' &&
    highlightedCard.value.id === props.remark.id
  )
})

const isOtherCardHighlighted = computed(() => {
  return !!highlightedCard.value.id && !highlighted.value
})

const notification = computed(() => {
  if (props.hideReadStatus) {
    return null
  }
  return store.getters.notificationsByRemarkId[props.remark.id]
})

const read = computed(() => {
  return notification.value ? notification.value.read : true
})

const remarkRead = computed(
  () =>
    read.value || (notification.value && !!notification.value.linkedCommentId)
)

const renderedTextWithOrdinal = computed(() => {
  if (props.ordinal && props.remark.renderedText.startsWith('<p>')) {
    return props.remark.renderedText.replace(
      '<p>',
      `<p><span>${props.ordinal}.</span> `
    )
  }

  return props.remark.renderedText
})

const disableInteraction = isCurrentWorkflowLocked()

// Both comment lists hand every comment the same card context. Only the unread
// offsets and the border differ between them.
const commentContext = computed(() => ({
  commentableId: props.remark.id,
  companyId: props.remark.companyId,
  ledgerId: props.remark.ledgerId,
  periodName: props.remark.privacyPeriodName,
  periodPrivate: props.remark.periodPrivate,
  disableInteraction,
}))

async function toggleRead() {
  if (notification.value) {
    await store.dispatch('toggleReadNotification', notification.value.id)
  } else {
    await store.dispatch('toggleRemarkFollow', props.remark.id)
    await store.dispatch('fetchNotifications', Silverfin.currentCompanyId)
  }
  window.eventHub.$emit('resetNotificationCount')
}

const isInCompanyWorkflowSubType = computed(() =>
  [RemarkContextTypes.ACCOUNT, RemarkContextTypes.RECONCILIATION_TEXT].includes(
    props.remark.contextType
  )
)

const remarkType = computed(() => (props.remark.todo ? 'todo' : 'note'))

const createdByBot = computed(() => !!props.remark.botName)

const authorName = computed(() => {
  if (createdByBot.value) {
    return props.remark.botName
  } else {
    return store.getters.userById(props.remark.authorId).name
  }
})

const attachments = computed(() =>
  pickAttachmentsByIds(
    store.getters.attachmentArray,
    props.remark.attachmentIds
  )
)

function deleteAttachment(attachmentId) {
  window.sfConfirm({
    onConfirm: () =>
      store.dispatch('deleteRemarkAttachment', {
        id: props.remark.id,
        attachmentId,
      }),
  })
}

const comments = computed(() => {
  const storeComments = orderBy(
    store.getters.commentsByIds(props.remark.commentIds),
    ['createdAt'],
    ['asc']
  )
  const result = []
  const hasLinkedComment = notification.value?.linkedCommentId !== null
  let linkedCommentSeen = false
  for (const comment of storeComments) {
    if (!comment) {
      continue
    }

    linkedCommentSeen ||= comment.id === notification.value?.linkedCommentId
    const commentRead = hasLinkedComment
      ? read.value || !linkedCommentSeen
      : read.value
    const isFirstUnread =
      !commentRead &&
      (result.length === 0 ||
        comment.id === notification.value?.linkedCommentId)
    result.push({
      ...comment,
      authorName: store.getters.userById(comment.authorId).name,
      editing: store.state.ui.editCommentIds.includes(comment.id),
      attachments: pickAttachmentsByIds(
        store.getters.attachmentArray,
        comment.attachmentIds
      ),
      allowedActions: {
        remove: comment.allowedActions.remove && !disableInteraction,
        edit:
          !comment.editing &&
          comment.allowedActions.edit &&
          !disableInteraction,
        openInContext: false,
        markAsRead: false,
      },
      read: commentRead,
      isFirstUnread,
    })
  }
  // In slim mode (presentation), show all comments regardless of presentationComments filter
  // In normal mode, filter by presentationComments if provided
  return !props.presentationComments || props.slim
    ? result
    : result.filter((c) => {
        return props.presentationComments.includes(c.id)
      })
})

const visibleComments = computed(() => {
  if (props.slim) return []
  const result = comments.value.slice(-2)
  return result.map((comment, i) => ({
    ...comment,
    first: i === 0,
    last: i !== result.length - 1,
  }))
})

const toggleableComments = computed(() => {
  // In presentation mode this list holds every comment, so it needs the same
  // last-comment flag the visible list carries. Note `last` is true for every
  // comment except the final one.
  if (props.slim)
    return comments.value.map((comment, i) => ({
      ...comment,
      last: i !== comments.value.length - 1,
    }))
  return comments.value.slice(0, -2)
})

function deleteCommentAttachment(commentId, attachmentId) {
  const comment = comments.value.find((c) => c.id === commentId)
  window.sfConfirm({
    onConfirm: () => {
      store.dispatch('editComment', [
        props.remark.id,
        'Remark',
        commentId,
        {
          fileAttrs: comment.attachmentIds
            .filter((id) => id !== attachmentId)
            .map((id) => ({ id })),
        },
      ])
    },
  })
}

function focusCommentForm({ commentableId, commentableType }) {
  if (commentableId === props.remark.id && commentableType === 'Remark') {
    store.dispatch('addNewCommentRemarkId', props.remark.id)
  }
}

function gotoPdfAnnotation() {
  if (!props.remark.pdfAnnotation) {
    return
  }

  const { xfdfId } = props.remark.pdfAnnotation

  if (props.remark.requestedForOwner) {
    window.eventHub.$emit('selectPdfAnnotation', xfdfId)
  } else {
    window.open(
      `${
        contextUrl.value
      }&annotationId=${xfdfId}&current_ledger_id=${window.Silverfin.currentLedgerId()}`,
      '_blank'
    )
  }
}

function clearHighlightedCard() {
  highlightCardStore.unsetHighlightedCard()
  window.eventHub.$emit('clearAnchor')
}

function handleClickOutside() {
  if (highlighted.value) {
    clearHighlightedCard()
  }
}

function scrollToElement() {
  if (props.remark.reportElementId) {
    window.eventHub.$emit('scrollToReportElement', {
      elementId: props.remark.reportElementId,
    })
  }
}

function handleContainerClick() {
  if (isOtherCardHighlighted.value) {
    clearHighlightedCard()
  }
}

const editingTags = ref(false)

function handleRemarkAction(action, event) {
  switch (action) {
    case 'updateTags':
      editingTags.value = true
      break
    case 'edit':
      openEditRemarkModal()
      break
    case 'openInContext':
      handleOpenInContext(event)
      break
    case 'toggleRead':
      toggleRead()
      break
    case 'remove':
      handleRemove()
      break
  }
}

function stopEditingTags() {
  editingTags.value = false
}

function handleOpenInContext(event) {
  trackClick('context-button')

  const shouldOpenInNewTab = event.metaKey || event.ctrlKey
  if (shouldOpenInNewTab) {
    window.open(contextUrl.value, '_blank')
  } else {
    const currentLocation = window.location.href

    window.Turbolinks.visit(contextUrl.value)

    const skipBackInContext = props.partOf === 'inbox' && theme.newLayout

    if (!skipBackInContext) {
      navigationStore.enableBackInContext(
        currentLocation,
        props.partOf,
        props.remark.todo ? 'todo' : 'note'
      )
    }
  }
}

async function destroyMessage(periodScope = null) {
  try {
    await api.destroy(props.remark.id, props.remark.companyId, periodScope)
    window.eventHub.$emit('refreshCommunicationData', {
      action: REMARK_ACTIONS.DESTROYED,
      remark: { id: props.remark.id },
    })
  } catch (e) {
    SfLog.warn('Error while removing message', { extra: { error: e } })
  }
}

function handleRemove() {
  if (props.remark.sticky) {
    window.modalCitizen.openModal('pinned-message-removal-modal', {
      onConfirm: async (allPeriods) => {
        try {
          if (allPeriods) {
            await destroyMessage()
          } else {
            await api.destroyOnwards(props.remark.id, props.remark.companyId)
            window.eventHub.$emit('refreshCommunicationData', {
              action: REMARK_ACTIONS.DESTROYED,
              remark: { id: props.remark.id },
            })
          }
        } catch (e) {
          SfLog.warn('Error while partially removing message', {
            extra: {
              error: e,
            },
          })
        }
      },
    })
  } else if (props.remark.recurringTodoTemplateId !== null) {
    window.modalCitizen.openModal('recurring-todo-removal-modal', {
      onConfirm: destroyMessage,
    })
  } else {
    window.sfConfirm({ onConfirm: destroyMessage })
  }
}

function handleCommentAction(event, id) {
  if (event === 'edit') {
    store.dispatch('addEditCommentId', id)
  } else if (event === 'remove') {
    window.sfConfirm({
      onConfirm: () =>
        store.dispatch('removeComment', [props.remark.id, 'Remark', id]),
    })
  }
}

function loadDraftFromLocalStorage() {
  if (remarkModalWasOpened.value === true) {
    reloadDraft()
    remarkModalWasOpened.value = false
  }
}

watch(
  () => comments.value.length,
  (newLength, oldLength) => {
    if (props.slim && oldLength === 0 && newLength > 0) {
      nextTick(() => commentList.value?.expand())
    }
  }
)

function startReply() {
  if (props.slim) commentList.value?.expand()
  store.dispatch('addNewCommentRemarkId', props.remark.id)
}

function openEditRemarkModal() {
  remarkModalWasOpened.value = true
  window.modalCitizen.openModal('edit-remark', {
    originalRemark: {
      ...props.remark,
      showTags: props.showTags,
      attachments,
      allRemarkTags: store.state.remarkTags
        ? store.state.remarkTags.allRemarkTags
        : null,
    },
    modalSize: props.editModalSize,
  })
}

function openEditRemarkModalExternal({ remarkId }) {
  if (props.remark.id === remarkId) {
    openEditRemarkModal()
  }
}

function clearHighlightedCardExternal() {
  if (highlighted.value) {
    clearHighlightedCard()
  }
}

async function toggleTodo() {
  await store.dispatch('toggleTodoDone', props.remark.id)
}

function trackClick(label) {
  if (props.partOf) {
    trackEvent(props.partOf, 'click', label)
  }
}

onMounted(() => {
  window.eventHub.$on('modal-closed', loadDraftFromLocalStorage)
  window.eventHub.$on('openEditRemarkModal', openEditRemarkModalExternal)
  window.eventHub.$on('clearHighlightedCard', clearHighlightedCardExternal)
  // focusCommentForm is received by both:
  // RemarkCard: if comment form is not rendered yet, we need to render it
  // CommentForm: if comment form is rendered, focus the correct comment form based on commentable type/id
  window.eventHub.$on('focusCommentForm', focusCommentForm)

  // if highlighted, and in the context of the pdf viewer, select the corresponding annotation on load
  if (highlighted.value && props.remark.requestedForOwner) {
    gotoPdfAnnotation()
  }
})

onBeforeUnmount(() => {
  window.eventHub.$off('modal-closed', loadDraftFromLocalStorage)
  window.eventHub.$off('openEditRemarkModal', openEditRemarkModalExternal)
  window.eventHub.$off('clearHighlightedCard', clearHighlightedCardExternal)
  window.eventHub.$off('focusCommentForm', focusCommentForm)
})
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.sf-btn-shadow-overlay {
  &,
  &:hover {
    box-shadow: 0 0 12px 12px -color('snow', 'light');
  }
}
</style>
